import state from './apps-state.js';
import * as render from './apps-render.js'
import * as api from './apps-api.js';
import * as apps from './apps.js';
import * as folder from './apps-folders.js';

// Helper function to update scroll zone visibility based on temporary screen state
function updateScrollZonesForTemporary(isFolder) {
    const onLeftTemporary = state.temporaryScreenSide === 'left';
    const onRightTemporary = state.temporaryScreenSide === 'right';
    
    if (isFolder) {
        const leftZone = document.getElementById('folderScrollZoneLeft');
        const rightZone = document.getElementById('folderScrollZoneRight');
        
        // Remove all active classes first
        if (leftZone) leftZone.classList.remove('active');
        if (rightZone) rightZone.classList.remove('active');
        
        // Only show the appropriate zone based on temporary screen side
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        } else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        } else {
            // Not on temporary - show both if dragging
            if (state.isDraggingIcon || (state.dragState && state.dragState.isKeyboardDrag)) {
                if (leftZone) leftZone.classList.add('active');
                if (rightZone) rightZone.classList.add('active');
            }
        }
    } else {
        const leftZone = document.getElementById('scrollZoneLeft');
        const rightZone = document.getElementById('scrollZoneRight');
        
        // Remove all active classes first
        if (leftZone) leftZone.classList.remove('active');
        if (rightZone) rightZone.classList.remove('active');
        
        // Only show the appropriate zone based on temporary screen side
        if (onLeftTemporary) {
            if (rightZone) rightZone.classList.add('active');
        } else if (onRightTemporary) {
            if (leftZone) leftZone.classList.add('active');
        } else {
            // Not on temporary - show both if dragging
            if (state.isDraggingIcon || (state.dragState && state.dragState.isKeyboardDrag)) {
                if (leftZone) leftZone.classList.add('active');
                if (rightZone) rightZone.classList.add('active');
            }
        }
    }
}

export function goToScreen(screenIndex) {
    const screensContainer = document.getElementById('appspaceScreens');
    const screenCount = screensContainer.children.length;
    
    const oldScreen = state.currentScreen;
    
    // Check if we're leaving a left temporary screen
    if (state.temporaryScreenSide === 'left' && oldScreen === 0 && screenIndex > 0) {
        const oldScreenElement = screensContainer.children[oldScreen];
        if (oldScreenElement && oldScreenElement.classList.contains('temporary')) {
            // Don't scroll - just collapse the temp screen
            oldScreenElement.style.maxWidth = '1px';
            oldScreenElement.style.minWidth = '1px';
            
            // Update state to reflect we're now on screen 0 (which will be the first real screen after removal)
            state.currentScreen = 0;
            
            // Wait for collapse animation, then remove
            setTimeout(() => {
                oldScreenElement.remove();
                state.temporaryScreenSide = null;
                
                // Restore page indicators
                const actualScreens = screensContainer.children.length;
                render.renderPageIndicators(actualScreens, 'pageIndicators');
            }, 300);
            
            return; // Don't proceed with normal scroll
        }
    }
    
    state.currentScreen = Math.max(0, Math.min(screenIndex, screenCount - 1));
    
    const offset = -state.currentScreen * 100;
    screensContainer.style.transform = `translateX(${offset}%)`;
    
    document.querySelectorAll('#pageIndicators .page-indicator').forEach((dot, index) => {
        dot.classList.toggle('active', index === state.currentScreen);
    });
    
    // Check if we're leaving a temporary screen (right side)
    if (state.temporaryScreenSide !== null && oldScreen !== state.currentScreen) {
        const oldScreenElement = screensContainer.children[oldScreen];
        if (oldScreenElement && oldScreenElement.classList.contains('temporary')) {
            // Right temp screen - wait for scroll animation then remove
            setTimeout(() => {
                oldScreenElement.remove();
                state.temporaryScreenSide = null;
                
                const actualScreens = screensContainer.children.length;
                render.renderPageIndicators(actualScreens, 'pageIndicators');
            }, 300);
        }
    }
}

export function goToFolderScreen(screenIndex) {
    const screensContainer = document.getElementById('folderScreens');
    const screenCount = screensContainer.children.length;
    
    const oldScreen = state.currentFolderScreen;
    
    // Check if we're leaving a left temporary folder screen
    if (state.temporaryScreenSide === 'left' && oldScreen === 0 && screenIndex > 0) {
        const tempScreenElement = screensContainer.children[0];
        if (tempScreenElement && tempScreenElement.classList.contains('temporary')) {
            // Collapse width back to 1px
            tempScreenElement.style.maxWidth = '1px';
            tempScreenElement.style.minWidth = '1px';
            
            // Wait for collapse animation, then remove
            setTimeout(() => {
                tempScreenElement.remove();
                state.temporaryScreenSide = null;
                
                // Stay at screen 0 (which is now the first real screen)
                state.currentFolderScreen = 0;
                
                // Restore page indicators
                const actualScreens = screensContainer.children.length;
                render.renderPageIndicators(actualScreens, 'folderPageIndicators');
            }, 300);
            
            return; // Don't proceed with normal scroll
        }
    }
    
    state.currentFolderScreen = Math.max(0, Math.min(screenIndex, screenCount - 1));
    
    // Calculate width consistently
    const layout = render.calculateLayout();
    const screenGap = layout.regionGapH * 3; // This is the gap BETWEEN screens
    
    // Get the actual screen width from the DOM (more reliable)
    const firstScreen = screensContainer.children[0];
    const calculatedScreenWidth = firstScreen ? firstScreen.offsetWidth : 
        (layout.regionWidth * layout.leaves + (layout.regionGapH * (layout.leaves - 1)) + (2 * layout.margin));
    
    // Calculate offset including gaps between screens
    // Total offset = (screenWidth + gap) * screenIndex
    const offset = -(state.currentFolderScreen * (calculatedScreenWidth + screenGap));
    
    screensContainer.style.transform = `translateX(${offset}px)`;
    
    document.querySelectorAll('#folderPageIndicators .page-indicator').forEach((dot, index) => {
        dot.classList.toggle('active', index === state.currentFolderScreen);
    });
    
    // Check if we're leaving a right temporary folder screen
    if (state.temporaryScreenSide === 'right' && oldScreen !== state.currentFolderScreen) {
        const oldScreenElement = screensContainer.children[oldScreen];
        if (oldScreenElement && oldScreenElement.classList.contains('temporary')) {
            // Wait for scroll animation, then remove
            setTimeout(() => {
                oldScreenElement.remove();
                state.temporaryScreenSide = null;
                
                const actualScreens = screensContainer.children.length;
                render.renderPageIndicators(actualScreens, 'folderPageIndicators');
            }, 300);
        }
    }
}

export async function createTemporaryScreenToLeft() {
    const screensContainer = document.getElementById('appspaceScreens');
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves * layout.bands;
    
    // Create all regions for the temporary screen
    const tempRegions = [];
    for (let i = 0; i < regionsPerScreen; i++) {
        tempRegions.push({ regionNum: i, icons: [] });
    }
    
    // Create temporary screen at the beginning with 1px width
    const screenDiv = document.createElement('div');
    screenDiv.className = 'appspace-screen temporary';
    screenDiv.innerHTML = render.renderScreen(tempRegions, false);
    screenDiv.style.maxWidth = '1px';
    screenDiv.style.minWidth = '1px';
    screenDiv.style.overflow = 'hidden';
    screenDiv.style.transition = 'max-width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
    screensContainer.insertBefore(screenDiv, screensContainer.firstChild);
    
    state.temporaryScreenSide = 'left';
    state.currentScreen = 0; // We're staying on the same visual screen
    
    // Update page indicators with + sign
    const actualScreens = screensContainer.querySelectorAll('.appspace-screen:not(.temporary)').length;
    render.renderPageIndicatorsWithTemp(actualScreens, 'left', 'pageIndicators', state.currentScreen);
    
    // Update scroll zone visibility for temporary screen
    updateScrollZonesForTemporary(false);
    
    // Force reflow
    screensContainer.offsetHeight;
    
    // Animate width to 100vw
    screenDiv.style.maxWidth = '100vw';
    screenDiv.style.minWidth = '100vw';
}

export function createTemporaryScreenToRight() {
    const screensContainer = document.getElementById('appspaceScreens');
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves * layout.bands;
    const actualScreens = screensContainer.querySelectorAll('.appspace-screen:not(.temporary)').length;
    const newRegionStart = actualScreens * regionsPerScreen;
    
    // Create all regions for the temporary screen
    const tempRegions = [];
    for (let i = 0; i < regionsPerScreen; i++) {
        tempRegions.push({ regionNum: newRegionStart + i, icons: [] });
    }
    
    // Create temporary screen at the end
    const screenDiv = document.createElement('div');
    screenDiv.className = 'appspace-screen temporary';
    screenDiv.innerHTML = render.renderScreen(tempRegions, false);
    screensContainer.appendChild(screenDiv);
    
    state.temporaryScreenSide = 'right';
    
    // Update page indicators with + sign
    render.renderPageIndicatorsWithTemp(actualScreens, 'right', 'pageIndicators', state.currentScreen);
    
    // Update scroll zone visibility for temporary screen
    updateScrollZonesForTemporary(false);
    
    // Move to the temporary screen
    goToScreen(screensContainer.children.length - 1);
}

export async function createTemporaryFolderScreenToLeft() {
    const screensContainer = document.getElementById('folderScreens');
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves; // Folders only have one band (region row)
    const folderId = state.openFolder ? state.openFolder.id : 0;
    
    // DON'T shift icons here - make it purely visual like main screen temporary
    // Icons will only be shifted if actually dropped on the temporary screen
    
    // Create temporary screen with regions [0, 1, ...] (purely visual)
    const tempRegions = [];
    for (let i = 0; i < regionsPerScreen; i++) {
        tempRegions.push({ regionNum: i, icons: [] });
    }
    
    // Get computed gap for folder dimensions
    const computedGapH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')) || state.config.gapHorizontal;
    
    const folderWidth = Math.min(
        window.innerWidth - 60,
        layout.regionWidth * layout.leaves + (computedGapH * 2 * (layout.leaves - 1))
    );
    
    // Match the height of existing folder screens
    const existingScreen = screensContainer.querySelector('.folder-screen:not(.temporary)');
    const folderHeight = existingScreen ? existingScreen.offsetHeight : layout.regionHeight;
    
    // Create temporary screen at the beginning with 1px width
    const screenDiv = document.createElement('div');
    screenDiv.className = 'folder-screen temporary';
    screenDiv.style.width = '1px';
    screenDiv.style.minWidth = '1px';
    screenDiv.style.maxWidth = '1px';
    screenDiv.style.flexBasis = '1px';
    screenDiv.style.height = folderHeight + 'px';
    screenDiv.style.overflow = 'hidden';
    screenDiv.style.transition = 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), flex-basis 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
    
    // Render the screen HTML
    const screenHTML = render.renderScreen(tempRegions, true);
    
    screenDiv.innerHTML = screenHTML;
    screensContainer.insertBefore(screenDiv, screensContainer.firstChild);
    
    state.temporaryScreenSide = 'left';
    state.currentFolderScreen = 0; // Stay on the same visual screen
    
    // Update page indicators with + sign
    const actualScreens = screensContainer.querySelectorAll('.folder-screen:not(.temporary)').length;
    render.renderPageIndicatorsWithTemp(actualScreens, 'left', 'folderPageIndicators', state.currentFolderScreen);
    
    // Update scroll zone visibility for temporary screen
    updateScrollZonesForTemporary(true);
    
    // Force reflow
    screensContainer.offsetHeight;
    
    // Animate width to full
    screenDiv.style.width = folderWidth + 'px';
    screenDiv.style.minWidth = folderWidth + 'px';
    screenDiv.style.maxWidth = folderWidth + 'px';
    screenDiv.style.flexBasis = folderWidth + 'px';
}

export function createTemporaryFolderScreenToRight() {
    const screensContainer = document.getElementById('folderScreens');
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves;
    const actualScreens = screensContainer.querySelectorAll('.folder-screen:not(.temporary)').length;
    const newRegionStart = actualScreens * regionsPerScreen;
    
    // Create all regions for the temporary screen
    const tempRegions = [];
    for (let i = 0; i < regionsPerScreen; i++) {
        tempRegions.push({ regionNum: newRegionStart + i, icons: [] });
    }
    
    // Get computed gap for folder dimensions
    const computedGapH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')) || state.config.gapHorizontal;
    
    const folderWidth = Math.min(
        window.innerWidth - 60,
        layout.regionWidth * layout.leaves + (computedGapH * 2 * (layout.leaves - 1))
    );
    
    // Match the height of existing folder screens
    const existingScreen = screensContainer.querySelector('.folder-screen:not(.temporary)');
    const folderHeight = existingScreen ? existingScreen.offsetHeight : layout.regionHeight;
    
    // Create temporary screen at the end
    const screenDiv = document.createElement('div');
    screenDiv.className = 'folder-screen temporary';

    // Render the screen HTML FIRST
    const screenHTML = render.renderScreen(tempRegions, true);

    // THEN set styles
    screenDiv.style.width = folderWidth + 'px';
    screenDiv.style.minWidth = folderWidth + 'px';
    screenDiv.style.flexBasis = folderWidth + 'px';
    screenDiv.style.height = folderHeight + 'px';

    // Set content and append
    screenDiv.innerHTML = screenHTML;
    screensContainer.appendChild(screenDiv);

    // NOW set widths on existing screens BEFORE scrolling
    const existingScreens = screensContainer.querySelectorAll('.folder-screen:not(.temporary)');
    existingScreens.forEach(screen => {
        screen.style.width = folderWidth + 'px';
        screen.style.minWidth = folderWidth + 'px';
        screen.style.flexBasis = folderWidth + 'px';
    });

    // Force a reflow to ensure widths are applied
    screensContainer.offsetWidth;

    state.temporaryScreenSide = 'right';
    
    // Update page indicators with + sign
    render.renderPageIndicatorsWithTemp(actualScreens, 'right', 'folderPageIndicators', state.currentFolderScreen);
    
    // Update scroll zone visibility for temporary screen
    updateScrollZonesForTemporary(true);
    
    // Move to the temporary screen
    const newScreenIndex = screensContainer.children.length - 1;
    goToFolderScreen(newScreenIndex);
}

export function removeTemporaryScreens() {
    const screensContainer = document.getElementById('appspaceScreens');
    const tempScreens = screensContainer.querySelectorAll('.appspace-screen.temporary');
    
    // If removing a left temporary screen, adjust currentScreen index
    const wasLeftTemp = state.temporaryScreenSide === 'left';
    
    tempScreens.forEach(screen => screen.remove());
    state.temporaryScreenSide = null;
    
    // Adjust currentScreen if we removed a left temporary screen
    if (wasLeftTemp && state.currentScreen > 0) {
        state.currentScreen = state.currentScreen - 1;
        
        // Disable transition to avoid double animation
        screensContainer.classList.add('dragging');
        
        // Update scroll position to match new index
        const offset = -state.currentScreen * 100;
        screensContainer.style.transform = `translateX(${offset}%)`;
        
        // Force reflow
        screensContainer.offsetHeight;
        
        // Re-enable transition
        screensContainer.classList.remove('dragging');
    }
    
    // Restore normal page indicators
    const actualScreens = screensContainer.children.length;
    render.renderPageIndicators(actualScreens, 'pageIndicators');
}

export function removeTemporaryFolderScreens() {
    const screensContainer = document.getElementById('folderScreens');
    const tempScreens = screensContainer.querySelectorAll('.folder-screen.temporary');
    
    // If removing a left temporary screen, adjust currentFolderScreen index
    const wasLeftTemp = state.temporaryScreenSide === 'left';
    
    tempScreens.forEach(screen => screen.remove());
    state.temporaryScreenSide = null;
    
    // Adjust currentFolderScreen if we removed a left temporary screen
    if (wasLeftTemp && state.currentFolderScreen > 0) {
        state.currentFolderScreen = state.currentFolderScreen - 1;
        
        // Disable transition to avoid double animation
        screensContainer.classList.add('dragging');
        
        // Update scroll position to match new index
        const layout = render.calculateLayout();
        const screenGap = layout.regionGapH * 3;
        const firstScreen = screensContainer.children[0];
        const calculatedScreenWidth = firstScreen ? firstScreen.offsetWidth : 
            (layout.regionWidth * layout.leaves + (layout.regionGapH * (layout.leaves - 1)) + (2 * layout.margin));
        const offset = -(state.currentFolderScreen * (calculatedScreenWidth + screenGap));
        screensContainer.style.transform = `translateX(${offset}px)`;
        
        // Force reflow
        screensContainer.offsetHeight;
        
        // Re-enable transition
        screensContainer.classList.remove('dragging');
    }
    
    // Restore normal page indicators
    const actualScreens = screensContainer.children.length;
    render.renderPageIndicators(actualScreens, 'folderPageIndicators');
}

// Check and handle empty screens
export async function cleanupEmptyScreens() {
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves * layout.bands;
    const rootIcons = window.icons.filter(icon => icon.kfolder === 0);
    
    if (rootIcons.length === 0) return;
    
    const maxRegion = Math.max(...rootIcons.map(icon => icon.region));
    const totalScreens = Math.ceil((maxRegion + 1) / regionsPerScreen);
    
    // Check each intermediate screen for emptiness
    for (let screenIndex = 1; screenIndex < totalScreens - 1; screenIndex++) {
        const screenStartRegion = screenIndex * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        const iconsInScreen = rootIcons.filter(icon => 
            icon.region >= screenStartRegion && icon.region <= screenEndRegion
        );
        
        if (iconsInScreen.length === 0) {
            console.log(`Intermediate screen ${screenIndex} is empty, marking for deletion`);
            return;
        }
    }
    
    // Check rightmost screen
    const rightmostScreenIndex = totalScreens - 1;
    const rightmostScreenStart = rightmostScreenIndex * regionsPerScreen;
    const rightmostScreenEnd = rightmostScreenStart + regionsPerScreen - 1;
    
    const rightmostScreenIcons = rootIcons.filter(icon =>
        icon.region >= rightmostScreenStart && icon.region <= rightmostScreenEnd
    );
    
    if (rightmostScreenIcons.length === 0 && totalScreens > 1) {
        console.log('Rightmost screen is empty, removing');
        const result = await api.deleteRegions(rightmostScreenStart, regionsPerScreen, 0);
        
        // Verify result
        if (result.success) {
            const expectedIcons = rootIcons.length;
            if (result.total_icons !== expectedIcons) {
                console.warn(`Icon count mismatch after deleteRegions: expected ${expectedIcons}, got ${result.total_icons}`);
            }
        }
        
        render.renderLaunchpad();
        return;
    }
}

// Similar function for folder screens
export async function cleanupEmptyFolderScreens() {
    if (!state.openFolder) return;
    
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves;
    const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
    
    if (folderIcons.length === 0) return;
    
    const maxRegion = Math.max(...folderIcons.map(icon => icon.region));
    const totalScreens = Math.ceil((maxRegion + 1) / regionsPerScreen);
    
    // Check intermediate screens
    for (let screenIndex = 1; screenIndex < totalScreens - 1; screenIndex++) {
        const screenStartRegion = screenIndex * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        const iconsInScreen = folderIcons.filter(icon => 
            icon.region >= screenStartRegion && icon.region <= screenEndRegion
        );
        
        if (iconsInScreen.length === 0) {
            console.log(`Intermediate folder screen ${screenIndex} is empty, marking for deletion`);
            return;
        }
    }
    
    // Check rightmost screen
    const rightmostScreenIndex = totalScreens - 1;
    const rightmostScreenStart = rightmostScreenIndex * regionsPerScreen;
    const rightmostScreenEnd = rightmostScreenStart + regionsPerScreen - 1;
    
    const rightmostScreenIcons = folderIcons.filter(icon =>
        icon.region >= rightmostScreenStart && icon.region <= rightmostScreenEnd
    );
    
    if (rightmostScreenIcons.length === 0 && totalScreens > 1) {
        console.log('Rightmost folder screen is empty, removing');
        const result = await api.deleteRegions(rightmostScreenStart, regionsPerScreen, state.openFolder.id);
        
        // Verify result
        if (result.success) {
            const expectedIcons = folderIcons.length;
            if (result.total_icons !== expectedIcons) {
                console.warn(`Icon count mismatch after deleteRegions: expected ${expectedIcons}, got ${result.total_icons}`);
            }
        }
        
        folder.openFolderView(state.openFolder);
        return;
    }
}

// Delete an empty screen and shift remaining screens
export async function deleteEmptyScreen(screenIndex, isFolder = false) {
    const layout = render.calculateLayout();
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = screenIndex * regionsPerScreen;
    const kfolder = isFolder ? state.openFolder.id : 0;
    
    console.log(`Deleting empty screen ${screenIndex}`);
    
    // Delete the regions - backend will verify they're empty
    const result = await api.deleteRegions(screenStartRegion, regionsPerScreen, kfolder);
    
    if (!result.success) {
        console.error('Failed to delete regions - they may not be empty');
        return;
    }
    
    // Collect icons to shift
    const iconsToShift = window.icons.filter(icon => {
        const iconContext = isFolder ? state.openFolder.id : 0;
        return icon.kfolder === iconContext && icon.region > screenStartRegion;
    });
    
    // Shift all icons after this screen down
    iconsToShift.forEach(icon => {
        icon.region -= regionsPerScreen;
    });
    
    // Batch update all shifted icons
    await api.updateIcons(iconsToShift);
    
    // Verify result
    const contextIcons = window.icons.filter(ic => ic.kfolder === kfolder);
    if (result.total_icons !== contextIcons.length) {
        console.warn(`Icon count mismatch after deleteEmptyScreen: expected ${contextIcons.length}, got ${result.total_icons}`);
    }
    
    // Re-render
    if (isFolder) {
        folder.openFolderView(state.openFolder);
        // Adjust current screen if needed
        if (state.currentFolderScreen >= screenIndex) {
            state.currentFolderScreen = Math.max(0, state.currentFolderScreen - 1);
            goToFolderScreen(state.currentFolderScreen);
        }
    } else {
        render.renderLaunchpad();
        // Adjust current screen if needed
        if (state.currentScreen >= screenIndex) {
            state.currentScreen = Math.max(0, state.currentScreen - 1);
            goToScreen(state.currentScreen);
        }
    }
}
